<script setup lang="ts">
import { computed } from 'vue'
import { MediaProcessingStatus, VirusScanStatus } from '@roadfootage/api-client'
import type { StaffFileProcessingResponse } from '@roadfootage/api-client'
import { formatNumber } from '@/utils/format'
import { STEP_LABELS } from '@/utils/pipeline-stage'

// Staff-only pipeline detail for one file: current step, estimated percent, status and scan result.
const props = defineProps<{ detail: StaffFileProcessingResponse | undefined }>()

const stepLabel = computed(() => (props.detail?.stage ? STEP_LABELS[props.detail.stage] : '—'))

const statusLabel = computed(() => {
  switch (props.detail?.status) {
    case MediaProcessingStatus.Complete:
      return 'Complete'
    case MediaProcessingStatus.Processing:
      return 'Processing'
    case MediaProcessingStatus.Failed:
      return 'Failed'
    default:
      return 'Pending'
  }
})

const statusColor = computed(() => {
  switch (props.detail?.status) {
    case MediaProcessingStatus.Complete:
      return 'positive'
    case MediaProcessingStatus.Failed:
      return 'negative'
    default:
      return 'info'
  }
})

// Only worth a line when it is not simply "pending" or "clean".
const scanNote = computed(() => {
  if (!props.detail) return ''
  const parts: string[] = []
  if (props.detail.virusScanStatus === VirusScanStatus.Threat) parts.push('Threat')
  if (props.detail.isQuarantined) parts.push('quarantined')
  return parts.join(', ')
})
</script>

<template>
  <div v-if="detail" class="q-mt-xs" data-testid="processing-step-detail">
    <div class="row items-center no-wrap justify-between text-caption">
      <span class="text-grey-8 ellipsis" data-testid="step-name">{{ stepLabel }}</span>
      <span class="text-grey-7 q-ml-xs" data-testid="step-percent"
        >{{ formatNumber(detail.percent ?? 0) }}%</span
      >
    </div>
    <q-linear-progress
      aria-label="Processing progress"
      rounded
      size="4px"
      :color="statusColor"
      track-color="grey-3"
      :value="(detail.percent ?? 0) / 100"
      class="q-my-xs"
    />
    <div class="text-caption" :class="`text-${statusColor}`" data-testid="step-status">
      {{ statusLabel }}
    </div>
    <div v-if="scanNote" class="text-caption text-negative" data-testid="step-scan">
      {{ scanNote }}
    </div>
  </div>
</template>
